import React, { useEffect, useState } from "react";
import "./Security.css";
import axios from "axios";
import { logHistory } from "../../utils/historyLogger";

function Security() {
  const [securityData, setSecurityData] = useState([]);
  const role = localStorage.getItem("role");
  const canEdit = role === "admin" || role === "superadmin";
  const canDelete = role === "superadmin";
  // Any logged-in role (user, admin, superadmin) can add data now.
  const canAdd = role === "user" || role === "admin" || role === "superadmin";

  const [search, setSearch] = useState("");
  const [editId, setEditId] = useState(null);

  const [formData, setFormData] = useState({
    date: "",
    shift: "Day",
    oicCount: "",
    oicPayment: "",
    jsoCount: "",
    jsoPayment: "",
    lsoCount: "",
    lsoPayment: "",
    ssoCount: "",
    ssoPayment: "",
    totalHeads: 0,
    totalCost: 0,
    remark: "",
  });

  useEffect(() => {
    fetchSecurity();
  }, []);

  const fetchSecurity = async () => {
    try {
      const res = await axios.get("https://hrpsc.swisstekaluminium.com/api/security");
      setSecurityData(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.log(err);
    }
  };

  // totalHeads = sum of all counts.
  // totalCost = sum of (count x manually entered per-person payment) for
  // each category - the user only ever types in counts and payments
  // (Rs. per person), the total is always derived automatically.
  const calculateData = (data) => {
    const oic = Number(data.oicCount || 0);
    const jso = Number(data.jsoCount || 0);
    const lso = Number(data.lsoCount || 0);
    const sso = Number(data.ssoCount || 0);

    const oicPayment = Number(data.oicPayment || 0);
    const jsoPayment = Number(data.jsoPayment || 0);
    const lsoPayment = Number(data.lsoPayment || 0);
    const ssoPayment = Number(data.ssoPayment || 0);

    const totalHeads = oic + jso + lso + sso;

    const totalCost =
      oic * oicPayment +
      jso * jsoPayment +
      lso * lsoPayment +
      sso * ssoPayment;

    return {
      ...data,
      totalHeads,
      totalCost,
    };
  };

  const handleChange = (e) => {
    const updatedData = {
      ...formData,
      [e.target.name]: e.target.value,
    };

    setFormData(calculateData(updatedData));
  };
    const handleSubmit = async () => {
    try {
      const payload = {
        date: formData.date || "",
        shift: formData.shift || "Day",
        oicCount: Number(formData.oicCount) || 0,
        oicPayment: Number(formData.oicPayment) || 0,
        jsoCount: Number(formData.jsoCount) || 0,
        jsoPayment: Number(formData.jsoPayment) || 0,
        lsoCount: Number(formData.lsoCount) || 0,
        lsoPayment: Number(formData.lsoPayment) || 0,
        ssoCount: Number(formData.ssoCount) || 0,
        ssoPayment: Number(formData.ssoPayment) || 0,
        totalHeads: Number(formData.totalHeads) || 0,
        totalCost: Number(formData.totalCost) || 0,
        remark: formData.remark || "",
      };

      if (editId) {
        await axios.put(
          `https://hrpsc.swisstekaluminium.com/api/security/${editId}`,
          payload
        );

        await logHistory(
          "Security",
          "Updated Security Record",
          "Security record updated"
        );

        alert("Updated Successfully");
      } else {
        await axios.post("https://hrpsc.swisstekaluminium.com/api/security", payload);

        await logHistory(
          "Security",
          "Added Security Record",
          "Security record added"
        );

        alert("Security Added");
      }

      fetchSecurity();
      clearForm();
    } catch (err) {
      console.log(err);
      alert(err.response?.data?.message || "Error Saving Security");
    }
  };

  const handleEdit = (item) => {
    setEditId(item.id);

    setFormData({
      date: item.date || "",
      shift: item.shift || "Day",
      oicCount: item.oicCount || "",
      oicPayment: item.oicPayment || "",
      jsoCount: item.jsoCount || "",
      jsoPayment: item.jsoPayment || "",
      lsoCount: item.lsoCount || "",
      lsoPayment: item.lsoPayment || "",
      ssoCount: item.ssoCount || "",
      ssoPayment: item.ssoPayment || "",
      totalHeads: item.totalHeads || 0,
      totalCost: item.totalCost || 0,
      remark: item.remark || "",
    });

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this security record?")) return;

    try {
      await axios.delete(`https://hrpsc.swisstekaluminium.com/api/security/${id}`);

      await logHistory(
        "Security",
        "Deleted Security Record",
        "Security record deleted"
      );

      fetchSecurity();
    } catch (err) {
      console.log(err);
      alert("Delete Failed");
    }
  };

  const clearForm = () => {
    setEditId(null);

    setFormData({
      date: "",
      shift: "Day",
      oicCount: "",
      oicPayment: "",
      jsoCount: "",
      jsoPayment: "",
      lsoCount: "",
      lsoPayment: "",
      ssoCount: "",
      ssoPayment: "",
      totalHeads: 0,
      totalCost: 0,
      remark: "",
    });
  };

  const downloadCSVReport = () => {
    if (securityData.length === 0) {
      alert("No security data to download");
      return;
    }

    const headers = Object.keys(securityData[0]);

    const rows = securityData.map((item) =>
      headers
        .map((field) => `"${String(item[field] ?? "").replace(/"/g, '""')}"`)
        .join(",")
    );

    const csvContent = [headers.join(","), ...rows].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = "security_report.csv";
    link.click();

    URL.revokeObjectURL(url);
  };

  const filteredData = securityData.filter((item) =>
    item.shift?.toLowerCase().includes(search.toLowerCase())
  );

  const totalRecords = securityData.length;

  const dayShift = securityData.filter((item) => item.shift === "Day").length;

  const nightShift = securityData.filter(
    (item) => item.shift === "Night"
  ).length;
    return (
    <div className="security-page">
      <div className="page-container">
        <h1 className="page-title">Security Management</h1>

        <div className="dashboard-cards">
          <div className="dashboard-card">
            <h3>Total Records</h3>
            <h1>{totalRecords}</h1>
          </div>

          <div className="dashboard-card">
            <h3>Day Shift</h3>
            <h1>{dayShift}</h1>
          </div>

          <div className="dashboard-card">
            <h3>Night Shift</h3>
            <h1>{nightShift}</h1>
          </div>
        </div>

        <div className="search-container">
          <input
            type="text"
            placeholder="Search Shift"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <button className="clear-btn" onClick={() => setSearch("")}>
            Clear
          </button>

          {/* Download available to every role - user downloads their own
              assigned-interface data, admin/superadmin download any interface */}
          <button className="add-btn" onClick={downloadCSVReport}>
            Download Report
          </button>
        </div>

        {/* Add form: user, admin, and superadmin can all add data now.
            The same form is reused for editing (editId gets set by
            handleEdit). */}
        {(canAdd || editId) && (
          <div className="form-container">
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
            />

            <select name="shift" value={formData.shift} onChange={handleChange}>
              <option value="Day">Day</option>
              <option value="Night">Night</option>
            </select>

            <input
              type="number"
              name="oicCount"
              placeholder="OIC Count"
              value={formData.oicCount}
              onChange={handleChange}
            />

            <input
              type="number"
              name="oicPayment"
              placeholder="OIC Payment (Rs. per person)"
              value={formData.oicPayment}
              onChange={handleChange}
            />

            <input
              type="number"
              name="jsoCount"
              placeholder="JSO Count"
              value={formData.jsoCount}
              onChange={handleChange}
            />

            <input
              type="number"
              name="jsoPayment"
              placeholder="JSO Payment (Rs. per person)"
              value={formData.jsoPayment}
              onChange={handleChange}
            />

            <input
              type="number"
              name="lsoCount"
              placeholder="LSO Count"
              value={formData.lsoCount}
              onChange={handleChange}
            />

            <input
              type="number"
              name="lsoPayment"
              placeholder="LSO Payment (Rs. per person)"
              value={formData.lsoPayment}
              onChange={handleChange}
            />

            <input
              type="number"
              name="ssoCount"
              placeholder="SSO Count"
              value={formData.ssoCount}
              onChange={handleChange}
            />

            <input
              type="number"
              name="ssoPayment"
              placeholder="SSO Payment (Rs. per person)"
              value={formData.ssoPayment}
              onChange={handleChange}
            />

            <input
              type="text"
              value={formData.totalHeads}
              readOnly
              placeholder="Total Heads"
            />

            <input
              type="text"
              value={`Rs. ${formData.totalCost}`}
              readOnly
              placeholder="Total Cost"
            />

            <input
              type="text"
              name="remark"
              placeholder="Remark"
              value={formData.remark}
              onChange={handleChange}
            />

            <button className="add-btn" onClick={handleSubmit}>
              {editId ? "Update" : "Add Security"}
            </button>

            <button className="clear-btn" onClick={clearForm}>
              Clear Form
            </button>
          </div>
        )}

        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Date</th>
                <th>Shift</th>
                <th>OIC</th>
                <th>JSO</th>
                <th>LSO</th>
                <th>SSO</th>
                <th>Total Heads</th>
                <th>Total Cost</th>
                <th>Remark</th>
                <th>Action</th>
              </tr>
            </thead>

            <tbody>
              {filteredData.map((item) => (
                <tr key={item.id}>
                  <td>{item.date}</td>

                  <td>
                    <span
                      className={
                        item.shift === "Day"
                          ? "status-completed"
                          : "status-pending"
                      }
                    >
                      {item.shift}
                    </span>
                  </td>

                  <td>{item.oicCount}</td>
                  <td>{item.jsoCount}</td>
                  <td>{item.lsoCount}</td>
                  <td>{item.ssoCount}</td>
                  <td>{item.totalHeads}</td>
                  <td>Rs. {item.totalCost}</td>
                  <td>{item.remark}</td>

                  <td>
                    {canEdit && (
                      <button
                        className="edit-btn"
                        onClick={() => handleEdit(item)}
                      >
                        Edit
                      </button>
                    )}

                    {canDelete && (
                      <button
                        className="delete-btn"
                        onClick={() => handleDelete(item.id)}
                      >
                        Delete
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default Security;